import {
	observable,
	computed,
	action,
	runInAction,
	reaction,
	IReactionDisposer,
} from "mobx";
import { Subscription } from "rxjs";
import { getCurrentCulture } from "@utils";

import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import type { RootStore } from "@state/stores/types";
import { RequestContextType } from "@utils/api-config/RequestContext";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";
import { findValidSubmitWallet } from "@utils/bet-slip/findValidSubmitWallet";
const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const  MyBetsService = lazy(loadFailPath, ()=>import("@services/my-bets/MyBetsService"));

export default class PlayedLiveEventsStore {
	@observable public count = 0;

	@computed public get isEnabled() {
		return this.rootStore.userAuthStore.user != null;
	}

	@observable public eventIds: string[] = [];
	@observable public isStoreInitialized: boolean = false;

	private playedOneClickTips: { id: string; eventId: string }[] = [];
	private rootStore: RootStore;
	private userAuthenticatedReaction: IReactionDisposer | null = null;
	private subscription: Subscription | null = null;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
	}

	//#region data fetching

	@action.bound
	async onInitialize() {
		this.disposeUserAuthenticatedReaction();

		if (!this.isEnabled) {
			this.eventIds = [];
			this.count = 0;
			this.isStoreInitialized = true;
			return;
		}

		this.userAuthenticatedReaction = reaction(
			() => ({ isEnabled: this.isEnabled }),
			() => {
				if (!this.isEnabled) {
					this.onDispose();
				}
			},
			{
				fireImmediately: true,
			}
		);

		//fetch played events for both users if secondary user exists
		try {
			let result: string[] = [];
			let walletId =
				findValidSubmitWallet(this.rootStore.userAuthStore)?.id || null;
			if (this.rootStore.userAuthStore.activeWalletId) {
				result = await (
					await MyBetsService
				).default.getEventIdsFromLiveBets(
					createApiRequestOptions(RequestContextType.Wallet, walletId)
				);
			}

			runInAction(() => {
				this.eventIds = result;
			});
		} catch (e) {
			console.error(e);
		}
		this.isStoreInitialized = true;

		this.initSubscription();
	}

	@action.bound
	private initSubscription() {
		this.disposeSubscription();

		if (this.eventIds.length === 0) {
			this.eventIds = [];
			this.count = 0;
			this.isStoreInitialized = true;

			return;
		}

		const filter = {
			subscriptionId: "live-played-events-count",
			count: {
				playedEventsCount: {
					liveStatus: 1,
					id: {
						eq: this.eventIds.map((i) => i),
					},
				},
			},
		};

		this.subscription = this.rootStore.hub.getCounts(filter).subscribe({
			next: (response) => {
				runInAction(() => {
					response.playedEventsCount = Number(
						response.playedEventsCount
					);
					if (!isNaN(response.playedEventsCount)) {
						this.count = response.playedEventsCount;
					}
					this.isStoreInitialized = true;
				});
			},
			error: (err) => {
				console.error("live from my bets count error", err);
				runInAction(() => {
					this.count = 0;
					this.isStoreInitialized = true;
				});
			},
		});
	}

	//#endregion data fetching

	//#region actions

	@action.bound
	public updateEvents(eventIds: string[]) {
		let haveAddedNewEventId = false;

		for (const eventId of eventIds) {
			if (!this.eventIds.includes(eventId)) {
				this.eventIds.push(eventId);
				haveAddedNewEventId = true;
			}
		}

		if (haveAddedNewEventId) {
			this.initSubscription();
		}
	}

	@action.bound
	public addPlayedOneClickTip(
		tip: PlayedLiveEventsStore["playedOneClickTips"][0]
	) {
		if (tip == null) {
			return;
		}

		this.playedOneClickTips.push(tip);
	}

	@action.bound
	public successfulOneClickTips(tipIds: string[]) {
		const eventIds = [];
		for (const tipId of tipIds) {
			const indexOfTip = this.playedOneClickTips.findIndex(
				(tip) => tip.id === tipId
			);

			if (indexOfTip === -1) {
				return;
			}

			const tip = this.playedOneClickTips[indexOfTip];
			this.playedOneClickTips.splice(indexOfTip, 1);

			eventIds.push(tip.eventId);
		}

		this.updateEvents(eventIds);
	}

	//#endregion actions

	//#region disposers

	@action.bound
	public onDispose() {
		this.disposeUserAuthenticatedReaction();
		this.disposeSubscription();
		this.eventIds = [];
		this.count = 0;
		this.isStoreInitialized = false;
	}

	@action.bound
	private disposeSubscription() {
		if (this.subscription != null) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}
	}

	@action.bound
	private disposeUserAuthenticatedReaction() {
		if (this.userAuthenticatedReaction != null) {
			this.userAuthenticatedReaction();
			this.userAuthenticatedReaction = null;
		}
	}

	//#endregion disposers
}
